/**
 * TacticalControls.tsx — Componenti UI della mappa SI.TA.C.
 * © 2024 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React, { useState } from 'react';
import { SelectModal } from '../common/SelectModal';
import type { SITACSymbolWithCategory } from './SITACSymbolSelector';
import type { WindDataSource } from '../../map/layers/WindOverlay';
import { decimalToDMS } from '../../map/utils/perimetroUtils';
import { EFFIS_LAYER_OPTIONS, type EFFISTemporalLayer, type EFFISStatus } from '../../map/layers/EFFISLayer';
import { COMBUSTIBILE_FACTORS } from './WindAnalysisLayer';

export type MapLayer = 'street' | 'satellite' | 'topo' | 'hillshade' | 'opentopomap' | 'hybrid';

export interface PlacementMode {
  active: boolean;
  symbol: SITACSymbolWithCategory | null;
}

// ─── Stili condivisi ─────────────────────────────────────────

const BTN_BASE: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.07)', color: '#f1f5f9',
  cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
  whiteSpace: 'nowrap',
};
const BTN_ACTIVE: React.CSSProperties = {
  ...BTN_BASE, background: '#ff4500', borderColor: '#ff4500',
  color: '#fff', boxShadow: '0 0 8px rgba(255,69,0,0.45)',
};
const BTN_INNESCO: React.CSSProperties = {
  ...BTN_BASE, background: 'rgba(220,38,38,0.18)',
  borderColor: '#dc2626', color: '#fca5a5',
};
const BTN_INNESCO_ACTIVE: React.CSSProperties = {
  ...BTN_INNESCO, background: '#dc2626', color: '#fff',
  boxShadow: '0 0 8px rgba(220,38,38,0.55)',
};
const SEP: React.CSSProperties = {
  width: 1, height: 22,
  background: 'rgba(255,255,255,0.15)',
  margin: '0 4px', flexShrink: 0,
};
const ACTION_BTN: React.CSSProperties = {
  padding: '4px 9px', borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.18)',
  background: 'rgba(255,255,255,0.07)', color: '#cbd5e1',
  cursor: 'pointer', fontSize: '0.74rem', fontWeight: 600,
};

// ─── TacticalToolbar ──────────────────────────────────────────

interface TacticalToolbarProps {
  perimetroMode: boolean;
  innescoMode?: boolean;
  puntoInnescoPresente?: boolean;
  mapLayer: MapLayer;
  placedSymbolsCount: number;
  showMobileSymbols: boolean;
  showSymbolSelector: boolean;
  readOnly: boolean;
  showLegend?: boolean;
  onToggleLegend?: () => void;
  onToggleMobileSymbols: () => void;
  onTogglePerimetroMode: () => void;
  onToggleInnescoMode?: () => void;
  onResetInnesco?: () => void;
  onSetInnescoByCoords?: () => void;
  onSetMapLayer: (layer: MapLayer) => void;
  onImportGeoJSON?: () => void;
  onPrint?: () => void;
  onExportMap: () => void;
  onClearMap: () => void;
  // EFFIS overlay
  showEFFIS?: boolean;
  effisLayer?: EFFISTemporalLayer;
  onToggleEFFIS?: () => void;
  onSetEffisLayer?: (layer: EFFISTemporalLayer) => void;
  // Ricerca/GPS integrata
  geoStatus?: string;
  onSearch?: () => void;
  isSearching?: boolean;
  onGPS?: () => void;
  resourceCounts?: { aerei: number; terrestri: number; simboli: number };
  // Elettrodotti
  showElettrodotti?: boolean;
  statoElettrodotti?: string;
  pericoloAereo?: boolean;
  onToggleElettrodotti?: () => void;
  onRicaricaElettrodotti?: () => void;
  onOpenOfflinePanel?: () => void;
  // Vedette
  vedettaMode?: boolean;
  vedettaCount?: number;
  vedetteDaPositionare?: Array<{ id: number; nominativo: string }>;
  onToggleVedettaMode?: () => void;
  onSelectVedettaToPlace?: (id: number) => void;
  // Mezzi terrestri
  mezzoTerrestreMode?: boolean;
  mezziTerrestriCount?: number;
  mezziDaPositionare?: Array<{ id: string; sigla: string; tipo: string }>;
  onToggleMezzoTerrestreMode?: () => void;
  onSelectMezzoToPlace?: (id: string) => void;
  // Posizione DOS
  haPosizioneDoS?: boolean;
  dosGpsLoading?: boolean;
  onAggiornaPosizioneDoS?: () => void;
  // Tablet di campo
  showPosizioniCampo?: boolean;
  campConnesso?: boolean;
  campoAbilitato?: boolean;
  posizioniCampoCount?: number;
  onTogglePosizioniCampo?: () => void;
}

export const TacticalToolbar: React.FC<TacticalToolbarProps> = ({
  perimetroMode, innescoMode = false, puntoInnescoPresente = false,
  mapLayer, placedSymbolsCount, showMobileSymbols, showSymbolSelector, readOnly,
  showLegend = false, onToggleLegend,
  onToggleMobileSymbols, onTogglePerimetroMode,
  onToggleInnescoMode, onResetInnesco, onSetInnescoByCoords,
  onSetMapLayer, onExportMap, onClearMap,
  showEFFIS = false, effisLayer = 'modis.ba.season', onToggleEFFIS, onSetEffisLayer,
  onImportGeoJSON, onPrint,
  vedettaMode = false, vedettaCount = 0, vedetteDaPositionare = [],
  onToggleVedettaMode, onSelectVedettaToPlace,
  mezzoTerrestreMode = false, mezziTerrestriCount = 0, mezziDaPositionare = [],
  onToggleMezzoTerrestreMode, onSelectMezzoToPlace,
  haPosizioneDoS = false, dosGpsLoading = false, onAggiornaPosizioneDoS,
  geoStatus, onSearch, isSearching, onGPS, resourceCounts,
  showElettrodotti = false, statoElettrodotti = 'idle', pericoloAereo = false,
  onToggleElettrodotti, onRicaricaElettrodotti,
  onOpenOfflinePanel,
  showPosizioniCampo = false, campConnesso = false, campoAbilitato = false,
  posizioniCampoCount = 0, onTogglePosizioniCampo,
}) => {
  const [showVedetteModal, setShowVedetteModal] = useState(false);
  const [showMezziModal, setShowMezziModal] = useState(false);
  const LAYERS: { value: MapLayer; label: string }[] = [
    { value: 'street',      label: '🗺️ Strade'              },
    { value: 'satellite',   label: '🛰️ Satellite'           },
    { value: 'hybrid',      label: '🛰️ Satellite + Strade'  },
    { value: 'topo',        label: '⛰️ Topo'                },
    { value: 'opentopomap', label: '🏔️ OpenTopoMap'         },
    { value: 'hillshade',   label: '🌄 Ombreggiatura'        },
  ];

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 10px',
      background: 'rgba(15, 23, 42, 0.97)',
      borderBottom: '1px solid rgba(255,255,255,0.10)',
      flexWrap: 'wrap', zIndex: 1300, position: 'relative',
    }}>
      <span style={{ color: '#f97316', fontWeight: 800, fontSize: '0.82rem', letterSpacing: 1, marginRight: 4 }}>
        SI.TA.C.
      </span>
      <div style={SEP} />

      {/* ── Ricerca / GPS integrati ── */}
      {geoStatus !== undefined && (
        <>
          <div style={{
            flex: '1 1 120px', maxWidth: 220,
            padding: '3px 8px',
            background: geoStatus.includes('✅') ? 'rgba(46,204,113,0.12)' :
                        geoStatus.includes('⚠️') ? 'rgba(255,193,7,0.12)' :
                        'rgba(52,152,219,0.12)',
            border: `1px solid ${geoStatus.includes('✅') ? 'rgba(46,204,113,0.4)' :
                                  geoStatus.includes('⚠️') ? 'rgba(255,193,7,0.4)' :
                                  'rgba(52,152,219,0.4)'}`,
            borderRadius: 4,
            fontSize: '0.72rem',
            color: '#e2e8f0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {geoStatus}
          </div>
          {onSearch && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', background: 'rgba(52,152,219,0.2)', borderColor: 'rgba(52,152,219,0.4)' }}
              onClick={onSearch}
              disabled={isSearching}
              title="Cerca indirizzo"
            >
              {isSearching ? '⏳' : '🔄'}
            </button>
          )}
          {onGPS && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', background: 'rgba(46,204,113,0.25)', borderColor: 'rgba(46,204,113,0.6)', color: '#6ee7b7', fontWeight: 600, gap: 3 }}
              onClick={onGPS}
              title="Rileva posizione GPS attuale"
            >
              📡 GPS
            </button>
          )}
          {resourceCounts && (
            <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
              ✈️{resourceCounts.aerei} 🚛{resourceCounts.terrestri}
            </span>
          )}
          <div style={SEP} />
        </>
      )}

      {!readOnly && (
        <>
          {/* ── Punto d'innesco ── */}
          {onToggleInnescoMode && (
            <button
              style={innescoMode ? BTN_INNESCO_ACTIVE : BTN_INNESCO}
              onClick={onToggleInnescoMode}
              title="Posiziona il punto di innesco cliccando sulla mappa"
            >★ Innesco</button>
          )}
          {onSetInnescoByCoords && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', padding: '4px 7px', background: 'rgba(220,38,38,0.10)', borderColor: '#dc2626', color: '#fca5a5' }}
              onClick={onSetInnescoByCoords}
              title="Posiziona il punto di innesco inserendo le coordinate GPS"
            >📐 Coord.</button>
          )}
          {puntoInnescoPresente && onResetInnesco && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', padding: '4px 7px', color: '#fca5a5' }}
              onClick={onResetInnesco}
              title="Rimuovi punto d'innesco"
            >✕ Innesco</button>
          )}
          <div style={SEP} />

          {/* ── Perimetro ── */}
          <button
            style={perimetroMode ? BTN_ACTIVE : BTN_BASE}
            onClick={onTogglePerimetroMode}
            title="Traccia il perimetro dell'incendio"
          >🔥 Perimetro</button>
          <div style={SEP} />

          {/* ── Posizione DOS ── */}
          {onAggiornaPosizioneDoS && (
            <>
              <button
                style={{
                  ...BTN_BASE,
                  background: dosGpsLoading
                    ? 'rgba(251,191,36,0.3)'
                    : haPosizioneDoS
                      ? 'rgba(251,191,36,0.2)'
                      : 'rgba(251,191,36,0.08)',
                  borderColor: dosGpsLoading ? 'rgba(251,191,36,0.6)' : 'rgba(251,191,36,0.5)',
                  color: dosGpsLoading ? '#94a3b8' : '#fcd34d',
                }}
                onClick={onAggiornaPosizioneDoS}
                disabled={dosGpsLoading}
                title={haPosizioneDoS ? 'Aggiorna posizione GPS del DOS' : 'Rileva posizione GPS del DOS'}
              >
                {dosGpsLoading ? '⌛ GPS...' : haPosizioneDoS ? '🎖️ DOS ✓' : '🎖️ Pos. DOS'}
              </button>
              <div style={SEP} />
            </>
          )}

          {/* ── Vedette ── */}
          {onToggleVedettaMode && (
            <>
              <button
                style={vedettaMode
                  ? { ...BTN_BASE, background: '#059669', borderColor: '#059669', color: '#fff', boxShadow: '0 0 8px rgba(5,150,105,0.5)' }
                  : { ...BTN_BASE, background: 'rgba(5,150,105,0.15)', borderColor: 'rgba(5,150,105,0.5)', color: '#6ee7b7' }
                }
                onClick={() => {
                  if (vedettaMode) {
                    onToggleVedettaMode();
                  } else if (vedetteDaPositionare.length > 0) {
                    setShowVedetteModal(true);
                  } else {
                    onToggleVedettaMode();
                  }
                }}
                title="Posiziona vedette sulla mappa"
              >
                🔭 Vedette{vedettaCount > 0 ? ` (${vedettaCount})` : ''}
              </button>
              <SelectModal
                isOpen={showVedetteModal}
                onClose={() => setShowVedetteModal(false)}
                title="🔭 Seleziona Vedetta da Posizionare"
                options={vedetteDaPositionare.map((v) => `🔭 ${v.nominativo}`)}
                onSelect={(label) => {
                  const nom = label.replace(/^🔭\s*/, '');
                  const found = vedetteDaPositionare.find((v) => v.nominativo === nom);
                  if (found) {
                    onSelectVedettaToPlace?.(found.id);
                    onToggleVedettaMode();
                  }
                  setShowVedetteModal(false);
                }}
              />
            </>
          )}
          <div style={SEP} />

          {/* ── Mezzi terrestri ── */}
          {onToggleMezzoTerrestreMode && (
            <>
              <button
                style={mezzoTerrestreMode
                  ? { ...BTN_BASE, background: '#b45309', borderColor: '#b45309', color: '#fff', boxShadow: '0 0 8px rgba(180,83,9,0.5)' }
                  : { ...BTN_BASE, background: 'rgba(180,83,9,0.15)', borderColor: 'rgba(180,83,9,0.5)', color: '#fcd34d' }
                }
                onClick={() => {
                  if (mezzoTerrestreMode) {
                    onToggleMezzoTerrestreMode();
                  } else if (mezziDaPositionare.length > 0) {
                    setShowMezziModal(true);
                  } else {
                    onToggleMezzoTerrestreMode();
                  }
                }}
                title="Posiziona mezzi terrestri sulla mappa"
              >
                🚒 Mezzi{mezziTerrestriCount > 0 ? ` (${mezziTerrestriCount})` : ''}
              </button>
              <SelectModal
                isOpen={showMezziModal}
                onClose={() => setShowMezziModal(false)}
                title="🚒 Seleziona Mezzo da Posizionare"
                options={mezziDaPositionare.map((m) => `${m.sigla}`)}
                onSelect={(label) => {
                  const found = mezziDaPositionare.find((m) => m.sigla === label);
                  if (found) {
                    onSelectMezzoToPlace?.(found.id);
                    onToggleMezzoTerrestreMode();
                  }
                  setShowMezziModal(false);
                }}
              />
            </>
          )}
          <div style={SEP} />

          {/* ── Simboli (toggle sidebar desktop + overlay mobile) ── */}
          <button
            style={showSymbolSelector ? BTN_ACTIVE : BTN_BASE}
            onClick={onToggleMobileSymbols}
            title={showSymbolSelector ? 'Nascondi pannello simboli' : 'Mostra pannello simboli'}
          >🗂️ Simboli</button>
          <div style={SEP} />
        </>
      )}

      {/* ── Layer mappa ── */}
      <select
        value={mapLayer}
        onChange={(e) => onSetMapLayer(e.target.value as MapLayer)}
        style={{ ...BTN_BASE, paddingRight: 6, background: 'rgba(255,255,255,0.08)', cursor: 'pointer' }}
      >
        {LAYERS.map((opt) => (
          <option key={opt.value} value={opt.value} style={{ background: '#1e293b' }}>
            {opt.label}
          </option>
        ))}
      </select>

      {/* ── EFFIS overlay ── */}
      {onToggleEFFIS && (
        <>
          <div style={SEP} />
          <button
            style={showEFFIS
              ? { ...BTN_BASE, background: 'rgba(153,27,27,0.85)', borderColor: '#dc2626', color: '#fecaca', boxShadow: '0 0 7px rgba(220,38,38,0.4)' }
              : { ...BTN_BASE, background: 'rgba(153,27,27,0.18)', borderColor: 'rgba(220,38,38,0.45)', color: '#fca5a5' }
            }
            onClick={onToggleEFFIS}
            title={showEFFIS ? 'Nascondi layer aree bruciate EFFIS' : 'Mostra layer aree bruciate EFFIS (JRC Copernicus)'}
          >🛰 EFFIS</button>
        </>
      )}

      {/* ── Elettrodotti ── */}
      {onToggleElettrodotti && (
        <>
          <div style={SEP} />
          <button
            style={showElettrodotti
              ? { ...BTN_BASE, background: 'rgba(204,0,0,0.85)', borderColor: '#CC0000', color: '#fff', boxShadow: '0 0 7px rgba(204,0,0,0.4)' }
              : { ...BTN_BASE, background: 'rgba(204,0,0,0.18)', borderColor: 'rgba(204,0,0,0.5)', color: '#fca5a5' }
            }
            onClick={onToggleElettrodotti}
            title={showElettrodotti ? 'Nascondi layer elettrodotti' : 'Mostra elettrodotti AT/AAT (OpenStreetMap)'}
          >
            {statoElettrodotti === 'loading' ? '⏳' : '⚡'} Elettrodotti
            {pericoloAereo && showElettrodotti && <span style={{ marginLeft: 4, color: '#ffd700', fontWeight: 900 }}>⚠</span>}
          </button>
          {showElettrodotti && statoElettrodotti === 'error' && onRicaricaElettrodotti && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', padding: '4px 7px', color: '#fca5a5' }}
              onClick={onRicaricaElettrodotti}
              title="Ricarica dati elettrodotti"
            >🔄</button>
          )}
        </>
      )}

      {/* ── Tablet di campo ── */}
      {onTogglePosizioniCampo && (
        <>
          <div style={SEP} />
          <button
            style={showPosizioniCampo
              ? { ...BTN_BASE, background: 'rgba(34,197,94,0.25)', borderColor: '#22c55e', color: '#86efac' }
              : { ...BTN_BASE, background: campoAbilitato ? 'rgba(34,197,94,0.08)' : 'rgba(100,116,139,0.15)', borderColor: 'rgba(34,197,94,0.3)', color: '#94a3b8' }
            }
            onClick={onTogglePosizioniCampo}
            title={campoAbilitato ? 'Mostra/nascondi posizioni tablet di campo' : 'Imposta Codice Operazione nel SITAC per attivare'}
          >
            📡 Campo{posizioniCampoCount > 0 ? ` (${posizioniCampoCount})` : ''}
            {showPosizioniCampo && (
              <span style={{ marginLeft: 4, fontSize: '0.65rem', color: campConnesso ? '#22c55e' : '#f59e0b' }}>
                {campConnesso ? '●' : '○'}
              </span>
            )}
          </button>
        </>
      )}

      {!readOnly && (
        <>
          <div style={SEP} />
          {onImportGeoJSON && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', background: 'rgba(59,130,246,0.18)', borderColor: 'rgba(59,130,246,0.45)', color: '#93c5fd' }}
              onClick={onImportGeoJSON}
              title="Importa perimetro da file GeoJSON"
            >📂 GeoJSON</button>
          )}
          {onPrint && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', background: 'rgba(99,102,241,0.18)', borderColor: 'rgba(99,102,241,0.5)', color: '#a5b4fc' }}
              onClick={onPrint}
              title="Stampa mappa con perimetro / Salva come PDF"
            >🖨️ Stampa PDF</button>
          )}
          <button style={{ ...BTN_BASE, fontSize: '0.72rem' }} onClick={onExportMap}>⬇️ Esporta</button>

          {/* ── Legenda ── */}
          <button
            style={showLegend ? { ...BTN_ACTIVE, fontSize: '0.72rem' } : { ...BTN_BASE, fontSize: '0.72rem' }}
            onClick={onToggleLegend}
            title="Mostra/nascondi legenda SITAC"
          >📖 Legenda</button>

          {placedSymbolsCount > 0 && (
            <button
              style={{ ...BTN_BASE, fontSize: '0.72rem', color: '#fca5a5', borderColor: '#991b1b' }}
              onClick={onClearMap}
            >🗑️ Pulisci</button>
          )}
        </>
      )}

      {/* ── Download offline ── */}
      {onOpenOfflinePanel && (
        <>
          <div style={SEP} />
          <button
            style={{ ...BTN_BASE, fontSize: '0.72rem', background: 'rgba(16,185,129,0.15)', borderColor: 'rgba(16,185,129,0.45)', color: '#6ee7b7' }}
            onClick={onOpenOfflinePanel}
            title="Scarica mappe per uso offline"
          >📥 Offline</button>
        </>
      )}

      {placedSymbolsCount > 0 && (
        <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {placedSymbolsCount} simbolo{placedSymbolsCount !== 1 ? 'i' : ''}
        </span>
      )}
    </div>
  );
};

// ─── PlacementBanner ──────────────────────────────────────────

interface PlacementBannerProps {
  placementMode: PlacementMode;
  innescoMode?: boolean;
  onCancel: () => void;
  onCancelInnesco?: () => void;
}

export const PlacementBanner: React.FC<PlacementBannerProps> = ({
  placementMode, innescoMode = false, onCancel, onCancelInnesco,
}) => {
  if (!placementMode.active && !innescoMode) return null;
  const isInn = innescoMode;
  const bg   = isInn ? 'rgba(185,28,28,0.96)' : 'rgba(30,64,175,0.96)';
  const msg  = isInn
    ? "★ Tocca la mappa per posizionare il PUNTO D'INNESCO"
    : `📍 Posiziona: ${placementMode.symbol?.descrizione ?? ''}`;
  const cancel = isInn ? onCancelInnesco : onCancel;

  return (
    <div style={{
      position: 'relative', zIndex: 1200, background: bg, color: '#fff',
      padding: '8px 14px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', fontSize: '0.82rem', fontWeight: 600,
      gap: 12, borderBottom: '1px solid rgba(255,255,255,0.12)',
    }}>
      <span>{msg}</span>
      {cancel && (
        <button onClick={cancel} style={{
          background: 'rgba(255,255,255,0.18)', border: 'none',
          color: '#fff', borderRadius: 5, padding: '3px 10px',
          cursor: 'pointer', fontWeight: 700,
        }}>Annulla</button>
      )}
    </div>
  );
};

// ─── PerimetroStatsOverlay ────────────────────────────────────

const VEGETATION_OPTIONS = [
  { value: '',                label: '— Tipo combustibile —' },
  { value: 'pascolo',         label: '🌾 Pascolo / erbacee secche' },
  { value: 'sterpaglia',      label: '🌿 Sterpaglia / incolto' },
  { value: 'macchia_bassa',   label: '🌱 Macchia bassa (cisto, lavanda)' },
  { value: 'macchia_alta',    label: '🌳 Macchia alta (lentisco, olivastro)' },
  { value: 'gariga',          label: '🔥 Gariga siciliana (ampelodesma)' },
  { value: 'pineta',          label: '🌲 Pineta (pino d\'Aleppo)' },
  { value: 'lecceta',         label: '🍂 Lecceta / querceto' },
  { value: 'eucalipteto',     label: '⚠️ Eucalipteto — ESTREMO' },
  { value: 'lettiera_conifere', label: '🍂 Lettiera di conifere' },
];

const COMBUSTIBILE_NOTE: Record<string, string> = {
  pascolo:           'Fuoco radente veloce. Fiamme basse, fronte ampio.',
  sterpaglia:        'Propagazione irregolare. Attenzione a cambi di vento.',
  macchia_bassa:     'Oli essenziali accelerano. Fiamme medie.',
  macchia_alta:      'Fiamme alte, avanzamento lento ma intenso.',
  gariga:            'Ampelodesma altamente infiammabile. Rischio elevato.',
  pineta:            'Rischio incendio di chioma e spotting (braci volanti).',
  lecceta:           'Fuoco di superficie lento. Braci durature nel sottobosco.',
  eucalipteto:       'PERICOLO MASSIMO — spotting fino a 2 km. Evacuare.',
  lettiera_conifere: 'Braci durature, riaccensioni frequenti dopo apparente spegnimento.',
};

// Velocità di avanzamento in m/min a vento dato (riferimento: pascolo = 35 m/min a 30 km/h)
function calcolaVelocita(windKmh: number, tipo: string): { mMin: number; m30: number; m60: number } {
  const factor = COMBUSTIBILE_FACTORS[tipo] ?? 1.0;
  const mMin = Math.round((windKmh / 30) * 35 * factor);
  return { mMin, m30: mMin * 30, m60: mMin * 60 };
}

function coloreVelocita(mMin: number): string {
  if (mMin >= 60) return '#ef4444'; // rosso — estremo
  if (mMin >= 35) return '#f97316'; // arancione — alto
  if (mMin >= 20) return '#fde047'; // giallo — medio
  return '#86efac';                  // verde — basso
}

interface PerimetroStatsOverlayProps {
  visible: boolean;
  onClose?: () => void;
  puntiCount: number;
  areaHa: number;
  perimetroKm: number;
  perimetroChiuso: boolean;
  gpsSurveyActive: boolean;
  propagationLabel: string;
  propagationDeg: number;
  windDescription: string;
  windSpeedKmh: number;
  windDataSource: WindDataSource;
  windSourceLabel: string;
  puntoInnesco?: [number, number] | null;
  tipoVegetazione?: string;
  noteOperative?: string;
  onTipoVegetazioneChange?: (v: string) => void;
  onNoteOperativeChange?: (v: string) => void;
  onStartGpsSurvey: () => void;
  onStopGpsSurvey: () => void;
  onChiudiPerimetro: () => void;
  onModificaPerimetro: () => void;
  onExportPerimetro: () => void;
  onResetPerimetro: () => void;
  // Step 3
  distanzaInnescoFronte?: number | null;
  innescoFuoriPerimetro?: boolean;
}

export const PerimetroStatsOverlay: React.FC<PerimetroStatsOverlayProps> = ({
  visible, onClose, puntiCount, areaHa, perimetroKm, perimetroChiuso,
  gpsSurveyActive, propagationLabel, propagationDeg,
  windDescription, windSpeedKmh, windDataSource, windSourceLabel,
  puntoInnesco, tipoVegetazione = '', noteOperative = '',
  onTipoVegetazioneChange, onNoteOperativeChange,
  onStartGpsSurvey, onStopGpsSurvey, onChiudiPerimetro,
  onModificaPerimetro, onExportPerimetro, onResetPerimetro,
  distanzaInnescoFronte = null, innescoFuoriPerimetro = false,
}) => {
  if (!visible) return null;

  const sourceTag =
    windDataSource === 'auto'   ? 'Reale'  :
    windDataSource === 'cached' ? 'Cache'  : 'Manuale';

  const areaM2 = Math.round(areaHa * 10000);

  return (
    <div style={{
      position: 'absolute', bottom: 14, left: 14, zIndex: 1100,
      background: 'rgba(15,23,42,0.96)', color: '#f1f5f9',
      borderRadius: 10, border: '1px solid rgba(255,69,0,0.35)',
      padding: '12px 14px', minWidth: 240, maxWidth: 290,
      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.79rem',
      maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
    }}>
      <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#f97316', borderBottom: '1px solid rgba(255,255,255,0.10)', paddingBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>🔥 Perimetro Incendio</span>
        {onClose && (
          <button
            onClick={onClose}
            title="Chiudi pannello"
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1, padding: '0 2px' }}
          >✕</button>
        )}
      </div>

      {puntoInnesco ? (
        <div style={{ color: '#fca5a5', fontWeight: 600, fontSize: '0.74rem', lineHeight: 1.5 }}>
          ★ {decimalToDMS(puntoInnesco[0], true)}<br />
          &nbsp;&nbsp;&nbsp;{decimalToDMS(puntoInnesco[1], false)}
        </div>
      ) : (
        <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.74rem' }}>
          ★ Innesco non impostato
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px' }}>
        <div style={{ color: '#94a3b8' }}>Vertici</div>
        <div style={{ fontWeight: 700 }}>{puntiCount}</div>
        <div style={{ color: '#94a3b8' }}>Area</div>
        <div style={{ fontWeight: 700 }}>
          {areaHa > 0 ? (
            <>{areaHa.toFixed(1)} ha<br /><span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>{areaM2.toLocaleString('it-IT')} m²</span></>
          ) : '—'}
        </div>
        <div style={{ color: '#94a3b8' }}>Perimetro</div>
        <div style={{ fontWeight: 700 }}>{perimetroKm > 0 ? `${(perimetroKm * 1000).toFixed(0)} m` : '—'}</div>
        {puntoInnesco && distanzaInnescoFronte !== null && (
          <>
            <div style={{ color: '#94a3b8' }}>★ → Fronte</div>
            <div style={{ fontWeight: 700, color: '#fbbf24' }}>
              {distanzaInnescoFronte >= 1000
                ? `${(distanzaInnescoFronte / 1000).toFixed(2)} km`
                : `${Math.round(distanzaInnescoFronte)} m`}
            </div>
          </>
        )}
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6 }}>
        <div style={{ color: '#94a3b8', marginBottom: 3 }}>Propagazione ({sourceTag} · {windSourceLabel})</div>
        <div style={{ fontWeight: 700, color: '#fde047' }}>
          ➤ {propagationLabel} ({propagationDeg}°) · {windDescription} ({Math.round(windSpeedKmh)} km/h)
        </div>
      </div>

      <div style={{
        padding: '4px 8px', borderRadius: 5,
        background: perimetroChiuso ? 'rgba(34,197,94,0.18)' : 'rgba(255,69,0,0.15)',
        color: perimetroChiuso ? '#86efac' : '#fca5a5',
        fontWeight: 700, textAlign: 'center', fontSize: '0.75rem',
      }}>
        {perimetroChiuso ? '✔ Perimetro chiuso' : '⚠ Perimetro aperto'}
      </div>

      {innescoFuoriPerimetro && (
        <div style={{
          padding: '6px 9px', borderRadius: 5,
          background: 'rgba(234,179,8,0.15)',
          border: '1px solid rgba(234,179,8,0.45)',
          color: '#fde047',
          fontWeight: 700, fontSize: '0.74rem', lineHeight: 1.5,
        }}>
          ⚠ Innesco fuori dal perimetro
          <br />
          <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.70rem' }}>
            Il punto di innesco non rientra nell&apos;area delimitata. Verificare la posizione o il perimetro.
          </span>
        </div>
      )}

      {/* ── Tipo combustibile — sempre visibile, influenza il triangolo di propagazione ── */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: 600, marginBottom: 2 }}>Combustibile</div>
        <select
          value={tipoVegetazione}
          onChange={(e) => onTipoVegetazioneChange?.(e.target.value)}
          style={{
            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
            color: tipoVegetazione ? '#f1f5f9' : '#64748b', borderRadius: 5,
            padding: '4px 6px', fontSize: '0.75rem', cursor: 'pointer', width: '100%',
          }}
        >
          {VEGETATION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value} style={{ background: '#1e293b' }}>{o.label}</option>
          ))}
        </select>

        {/* Dati velocità — visibili quando c'è un combustibile selezionato ── */}
        {tipoVegetazione && (() => {
          const { mMin, m30, m60 } = calcolaVelocita(windSpeedKmh, tipoVegetazione);
          const colore = coloreVelocita(mMin);
          const nota = COMBUSTIBILE_NOTE[tipoVegetazione];
          return (
            <div style={{
              background: 'rgba(0,0,0,0.25)', borderRadius: 6,
              border: `1px solid ${colore}55`, padding: '7px 9px',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#94a3b8', fontSize: '0.7rem' }}>Velocità avanzamento</span>
                <span style={{ color: colore, fontWeight: 800, fontSize: '0.92rem' }}>{mMin} m/min</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px', fontSize: '0.72rem' }}>
                <span style={{ color: '#94a3b8' }}>30 minuti</span>
                <span style={{ fontWeight: 700 }}>
                  {m30 >= 1000 ? `${(m30 / 1000).toFixed(1)} km` : `${m30} m`}
                </span>
                <span style={{ color: '#94a3b8' }}>60 minuti</span>
                <span style={{ fontWeight: 700 }}>
                  {m60 >= 1000 ? `${(m60 / 1000).toFixed(1)} km` : `${m60} m`}
                </span>
              </div>
              {nota && (
                <div style={{ fontSize: '0.68rem', color: '#94a3b8', lineHeight: 1.4, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 4, marginTop: 2 }}>
                  {nota}
                </div>
              )}
            </div>
          );
        })()}

        {/* Note operative — visibili solo con perimetro disegnato ── */}
        {puntiCount >= 3 && (
          <>
            <textarea
              value={noteOperative}
              onChange={(e) => onNoteOperativeChange?.(e.target.value.slice(0, 1000))}
              placeholder="Note operative..."
              maxLength={1000}
              rows={3}
              style={{
                background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)',
                color: '#f1f5f9', borderRadius: 5, padding: '4px 6px',
                fontSize: '0.74rem', resize: 'vertical', width: '100%',
                boxSizing: 'border-box',
              }}
            />
            {noteOperative.length > 900 && (
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', textAlign: 'right' }}>
                {noteOperative.length}/1000
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 8 }}>
        {!gpsSurveyActive ? (
          <button onClick={onStartGpsSurvey} style={ACTION_BTN}>📡 GPS</button>
        ) : (
          <button onClick={onStopGpsSurvey} style={{ ...ACTION_BTN, background: 'rgba(239,68,68,0.25)', color: '#fca5a5' }}>
            ⏹ Stop GPS
          </button>
        )}
        {!perimetroChiuso && puntiCount >= 3 && (
          <button onClick={onChiudiPerimetro} style={{ ...ACTION_BTN, background: 'rgba(34,197,94,0.18)', color: '#86efac' }}>
            ✔ Chiudi
          </button>
        )}
        {perimetroChiuso && (
          <button onClick={onModificaPerimetro} style={ACTION_BTN}>✏️ Modifica</button>
        )}
        {puntiCount >= 3 && (
          <button onClick={onExportPerimetro} style={ACTION_BTN}>⬇️ GeoJSON</button>
        )}
        <button onClick={onResetPerimetro} style={{ ...ACTION_BTN, color: '#fca5a5', borderColor: '#991b1b' }}>
          🗑️ Reset
        </button>
      </div>
    </div>
  );
};

// ─── PerimetroModeBadge ───────────────────────────────────────

export const PerimetroModeBadge: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute', top: 10, left: '50%',
      transform: 'translateX(-50%)', zIndex: 1150,
      background: 'rgba(255,69,0,0.92)', color: '#fff',
      padding: '4px 14px', borderRadius: 20,
      fontWeight: 800, fontSize: '0.75rem', letterSpacing: 1.5,
      pointerEvents: 'none', boxShadow: '0 2px 12px rgba(255,69,0,0.5)',
    }}>
      🔥 MODALITÀ PERIMETRO
    </div>
  );
};

// ─── InnescoModeBadge ─────────────────────────────────────────

export const InnescoModeBadge: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute', top: 10, left: '50%',
      transform: 'translateX(-50%)', zIndex: 1150,
      background: 'rgba(185,28,28,0.95)', color: '#fff',
      padding: '4px 14px', borderRadius: 20,
      fontWeight: 800, fontSize: '0.75rem', letterSpacing: 1.5,
      pointerEvents: 'none', boxShadow: '0 2px 12px rgba(185,28,28,0.6)',
    }}>
      ★ POSIZIONA PUNTO D&apos;INNESCO
    </div>
  );
};

// ─── EFFISLegendBadge ─────────────────────────────────────────

export const EFFISLegendBadge: React.FC<{
  visible: boolean;
  layerLabel?: string;
  effisStatus?: EFFISStatus;
  effisLayer?: EFFISTemporalLayer;
  onSetEffisLayer?: (layer: EFFISTemporalLayer) => void;
}> = ({ visible, layerLabel, effisStatus = 'idle', effisLayer, onSetEffisLayer }) => {
  if (!visible) return null;

  const statusIcon =
    effisStatus === 'loading' ? (
      <span style={{
        display: 'inline-block', width: 12, height: 12,
        border: '2px solid rgba(251,191,36,0.4)', borderTopColor: '#fbbf24',
        borderRadius: '50%', flexShrink: 0,
        animation: 'effis-spin 0.8s linear infinite',
      }} />
    ) : effisStatus === 'ok' ? (
      <span style={{ color: '#4ade80', fontSize: '0.78rem', flexShrink: 0 }}>✓</span>
    ) : effisStatus === 'error' ? (
      <span style={{ color: '#f87171', fontSize: '0.78rem', flexShrink: 0 }}>✕</span>
    ) : (
      <span style={{
        display: 'inline-block', width: 12, height: 12, borderRadius: 2,
        background: '#7f1d1d', border: '1px solid #dc2626', flexShrink: 0,
      }} />
    );

  return (
    <>
      <style>{`@keyframes effis-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
        position: 'absolute', bottom: 36, left: 14, zIndex: 1050,
        background: 'rgba(15,23,42,0.90)', color: '#f1f5f9',
        padding: '6px 10px', borderRadius: 6,
        fontSize: '0.69rem', fontWeight: 600,
        border: `1px solid ${effisStatus === 'error' ? 'rgba(248,113,113,0.5)' : 'rgba(220,38,38,0.4)'}`,
        display: 'flex', alignItems: 'center', gap: 7,
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      }}>
        {statusIcon}
        <span>
          EFFIS — Aree bruciate<br />
          {effisStatus === 'error'
            ? <span style={{ color: '#f87171', fontWeight: 400 }}>Server non raggiungibile</span>
            : effisStatus === 'loading'
              ? <span style={{ color: '#fbbf24', fontWeight: 400 }}>Caricamento...</span>
              : <span style={{ color: '#94a3b8', fontWeight: 400 }}>© JRC Copernicus</span>
          }
        </span>
        {onSetEffisLayer && effisLayer && (
          <select
            value={effisLayer}
            onChange={(e) => onSetEffisLayer(e.target.value as EFFISTemporalLayer)}
            style={{
              background: 'rgba(153,27,27,0.6)', border: '1px solid rgba(220,38,38,0.5)',
              color: '#fecaca', borderRadius: 4, fontSize: '0.69rem',
              padding: '2px 4px', cursor: 'pointer', marginLeft: 4,
            }}
            title="Cambia periodo EFFIS"
          >
            {EFFIS_LAYER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} style={{ background: '#1e293b' }}>{o.label}</option>
            ))}
          </select>
        )}
      </div>
    </>
  );
};

// ─── HillshadeBadge ───────────────────────────────────────────

export const HillshadeBadge: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute', bottom: 36, right: 10, zIndex: 1050,
      background: 'rgba(15,23,42,0.82)', color: '#94a3b8',
      padding: '3px 8px', borderRadius: 5,
      fontSize: '0.68rem', fontWeight: 600,
      pointerEvents: 'none', letterSpacing: 0.5,
    }}>
      🌄 HILLSHADE ATTIVO
    </div>
  );
};
